<template>
  <main>
    <lazy-cms-section
      v-if="cmsSections?.['above-iframe']"
      class="mb-4"
      :section="cmsSections?.['above-iframe']"
    />
    <div class="my-8 container ">
      <base-iframe
        allow="geolocation"
        data-test-id="store-locator"
        :height="frameHeight"
        :params
        :src
        :title="$t.storeLocator"
        width="100%"
        @message="onMessage"
      />
    </div>
    <lazy-cms-section
      v-if="cmsSections?.['below-iframe']"
      class="mb-16"
      :section="cmsSections?.['below-iframe']"
    />
  </main>
</template>

<script lang="ts" setup>
import type { GTMEvent } from '#types/gtm'

interface StoreLocatorMessageEvent {
  data?: {
    command: string
    body?: { dataLayerData?: GTMEvent }
  } | string
}

const MIN_FRAME_HEIGHT = 900

const { $gtm, $locale, $t } = useNuxtApp()
const { gtm } = useRuntimeConfig().public.features
const { defaultSrc, addressLine, localizedSrc = [], capitalizeCountryCode } = useAppConfig().pages.storeLocator
const { query } = useRoute()

const frameHeight = ref<string | number>(MIN_FRAME_HEIGHT)

const cmsSections = (await useCms().getSections.storeLocator()).data.value?.content.sectionsMap

const [lang, countryCode] = $locale.split('-')

const src = localizedSrc.find(({ locales }) => locales.includes($locale))?.src.replace('{lang}', lang) || defaultSrc

const desiredLang = localizedSrc.find(({ locales }) => locales.includes($locale))?.lang

// If URL already has country we should not add it again
// Useful for UK as we store it as GB but store locator for TBL, TNF, Vans expects UK code
const srcWithCountry = new URLSearchParams(src).has('country')

// Older Vans NORA store locator breaks if you add the country param
const isLegacyLocator = src.includes('locations.vans.com')

const params = {
  ...query,
  ...(addressLine?.[countryCode] && { addressline: addressLine[countryCode] }),
  ...(srcWithCountry || isLegacyLocator
    ? {}
    : {
        country: capitalizeCountryCode ? countryCode.toUpperCase() : countryCode
      }),
  lang: useFormattedLocale(desiredLang)
}

/**
 * Handle iframe resize events
 * Data can be either a height value or an event name and height value separated by '//'
 * e.g. 'w2gi:iframeHeightUpdated//900' or '1000//768'
 * @param size
 */
const resize = (size: string) => {
  const [eventOrHeight, height] = size.split('//')
  frameHeight.value = (eventOrHeight === 'w2gi:iframeHeightUpdated' ? height : eventOrHeight) || MIN_FRAME_HEIGHT
}

const onMessage = (e: StoreLocatorMessageEvent) => {
  if (isString(e.data)) {
    resize(e.data)
    return
  }

  if (gtm.enabled && e.data?.command === 'dataLayerPush' && e.data.body?.dataLayerData)
    $gtm.pushEventObj(e.data.body.dataLayerData)
}
</script>
